import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BreadcrumbNav } from '../components/BreadcrumbNav';
import { TenantCard } from '../components/TenantCard';
import { getStateByCode } from '../data/brazilianStates';
import { getTenantsByState, getMunicipalitiesByState, FirestoreMunicipality } from '../services/portalService';
import { TenantListingCard } from '../../../core/types/portal';
import { Filter, Grid, List, MapPin } from 'lucide-react';

export const StateDirectoryPage: React.FC = () => {
  const { state } = useParams<{ state: string }>();
  // ... state declarations
  const [allTenants, setAllTenants] = useState<TenantListingCard[]>([]);
  const [municipalities, setMunicipalities] = useState<FirestoreMunicipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Filtros
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  
  const stateData = state ? getStateByCode(state) : null;

  useEffect(() => {
    if (state) {
      loadData(state);
    }
  }, [state]);
  
  const loadData = async (stateCode: string) => {
    setLoading(true);
    try {
      const [tenantsData, municipalitiesData] = await Promise.all([
        getTenantsByState(stateCode),
        getMunicipalitiesByState(stateCode)
      ]);
      setAllTenants(tenantsData);
      setMunicipalities(municipalitiesData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTypeFilter = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Filtragem local
  const displayedTenants = allTenants.filter(tenant => {
    // Filtro por cidade
    if (selectedCity && tenant.location.city !== selectedCity) return false;
    
    // Filtro por tipo (case insensitive para garantir)
    if (selectedTypes.length > 0) {
      const tenantTypeNormalized = tenant.tenantType.toLowerCase();
      // Mapeamento simples de rótulos para valores de tipo
      const typeMatches = selectedTypes.some(selectedType => {
        const normalizedSelected = selectedType.toLowerCase()
          .replace('pousada', 'pousada')
          .replace('hotel', 'hotel')
          .replace('barco hotel', 'barco-hotel')
          .replace('guia', 'guia')
          .replace('agência', 'agencia');
        return tenantTypeNormalized === normalizedSelected;
      });
      if (!typeMatches) return false;
    }
    
    return true;
  });

  if (!stateData) {
    return (
      <div className="min-h-screen bg-brand-midnight flex flex-col items-center justify-center p-6 bg-glow-mesh-dark">
        <div className="text-center max-w-md">
          <div className="text-8xl mb-8 grayscale opacity-20">📍</div>
          <h1 className="text-4xl font-black text-white mb-4 font-display">Estado não encontrado</h1>
          <p className="text-white/40 mb-8 font-medium italic">O destino solicitado parece estar fora do nosso radar no momento.</p>
          <button 
            onClick={() => window.history.back()}
            className="text-brand-gold hover:underline font-black uppercase tracking-widest text-xs"
          >
            ← Voltar para Explorer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      {/* Header Content */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="container mx-auto px-6 py-8">
          <BreadcrumbNav 
            items={[
              { label: 'Estados', path: '/estados' },
              { label: stateData.name }
            ]}
          />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-white mb-2 font-display">
                {stateData.emoji || '📍'} {stateData.name}
              </h1>
              <p className="text-white/40 font-bold uppercase tracking-widest text-xs">
                {loading ? '...' : `${displayedTenants.length} empresas encontradas`}
                {selectedCity && ` em ${selectedCity}`}
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                aria-label="Visualização em grade"
                title="Visualização em grade"
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                aria-label="Visualização em lista"
                title="Visualização em lista"
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filtros e Listagem */}
      <div className="container mx-auto px-6 py-8 flex-grow">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar de Filtros */}
          <div className="lg:col-span-1">
            <div className="bg-brand-charcoal/30 rounded-2xl p-6 border border-white/10 sticky top-6">
              <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 mb-6 flex items-center gap-2">
                <Filter size={14} className="text-brand-gold" />
                Filtros
              </h3>

              {/* Filtro por Cidade */}
              <div className="mb-6">
                  <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <MapPin size={14} />
                  Cidade
                </h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={loading}
                  title="Selecionar Cidade"
                >
                  <option value="">Todas as cidades</option>
                  {municipalities.map(mun => (
                    <option key={mun.code} value={mun.name}>
                      {mun.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Filtro por Tipo */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Tipo de Empresa</h4>
                <div className="space-y-2">
                  {['Pousada', 'Hotel', 'Barco Hotel', 'Guia', 'Agência'].map(type => (
                    <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:text-brand-orange transition-colors">
                      <input 
                        type="checkbox" 
                        className="rounded text-brand-gold focus:ring-brand-gold border-white/20 bg-white/5"
                        checked={selectedTypes.includes(type)}
                        onChange={() => toggleTypeFilter(type)}
                      />
                      <span className="text-white/60 font-medium">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Filtro por Categoria */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Tipo de Pesca</h4>
                <div className="space-y-2">
                  {['Pesca Embarcada', 'Pesca de Barranco', 'Fly Fishing'].map(category => (
                    <label key={category} className="flex items-center gap-2 text-sm text-white/20 cursor-not-allowed">
                      <input type="checkbox" className="rounded border-white/10 bg-white/5" disabled />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Filtro por Avaliação */}
              <div>
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Avaliação Mínima</h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-sm text-white/20 cursor-not-allowed"
                  disabled
                  title="Avaliação Mínima"
                >
                  <option>Todas</option>
                  <option>4+ estrelas</option>
                  <option>4.5+ estrelas</option>
                  <option>5 estrelas</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Grid de Empresas */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
                <p className="mt-4 text-white/60 font-medium">Carregando empresas e cidades...</p>
              </div>
            ) : displayedTenants.length === 0 ? (
              <div className="bg-white/5 rounded-3xl p-12 text-center border border-white/10 shadow-xl">
                <div className="text-6xl mb-4 grayscale opacity-50">🎣</div>
                <h3 className="text-xl font-black text-white mb-2">
                  Nenhuma empresa encontrada
                </h3>
                <p className="text-white/40 mb-6 font-medium">
                  {selectedCity 
                    ? `Não encontramos empresas em ${selectedCity}. Tente buscar em outra cidade.`
                    : `Ainda não temos empresas cadastradas em ${stateData.name}.`
                  }
                </p>
                <button 
                  onClick={() => setSelectedCity('')}
                  className="mr-4 text-brand-gold hover:underline font-bold"
                >
                  Limpar filtros
                </button>
                <a 
                  href="/cadastrar-empresa"
                  className="inline-block bg-brand-orange text-white font-black px-8 py-4 rounded-xl hover:shadow-glow-orange transition-all font-display uppercase tracking-widest text-xs"
                >
                  Seja o Primeiro a Cadastrar
                </a>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-6' : 'space-y-4'}>
                {displayedTenants.map(tenant => (
                  <TenantCard 
                    key={tenant.id} 
                    tenant={tenant}
                    variant={viewMode === 'list' ? 'compact' : 'default'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
